import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, differenceInMinutes, isWithinInterval } from 'date-fns';
import { Clock, CheckCircle, AlertCircle, XCircle, Calendar } from 'lucide-react';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';
import { ShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import { StyleVariant } from '@/lib/scheduling/shift-status-styles';

interface InProgressShift {
  id: string;
  start_time: string;
  end_time: string;
  actual_arrival_time?: string | null;
}

interface InProgressShiftDisplayProps {
  employeeId: string;
  businessId: string;
  onNavigateToShifts?: () => void;
}

const InProgressShiftDisplay: React.FC<InProgressShiftDisplayProps> = ({ employeeId, businessId, onNavigateToShifts }) => {
  const [inProgressShift, setInProgressShift] = useState<InProgressShift | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Timezone-aware shift status service
  const shiftStatusService = useShiftStatusService();

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchInProgressShift = async () => {
      if (!employeeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const now = new Date();
        
        // Fetch shifts that might be in progress (today's shifts and nearby)
        const { data: shiftsData, error: shiftsError } = await supabase
          .from('shifts')
          .select('id, start_time, end_time')
          .eq('employee_id', employeeId)
          .gte('start_time', new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString()) // 24 hours ago
          .lte('start_time', new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString()) // 24 hours ahead
          .order('start_time', { ascending: true });

        if (shiftsError) {
          console.error('Error fetching shifts:', shiftsError);
          setInProgressShift(null);
          return;
        }

        // Find shift that is currently in progress
        const currentShift = shiftsData?.find(shift => {
          const shiftStart = parseISO(shift.start_time);
          const shiftEnd = parseISO(shift.end_time);
          
          return isWithinInterval(now, { start: shiftStart, end: shiftEnd });
        });

        if (currentShift) {
          // Fetch arrival time for this shift
          const { data: timeLogsData } = await supabase
            .from('time_logs')
            .select('scanned_at')
            .eq('employee_id', employeeId)
            .eq('event_type', 'arrival')
            .gte('scanned_at', parseISO(currentShift.start_time).toISOString())
            .lte('scanned_at', parseISO(currentShift.end_time).toISOString())
            .order('scanned_at', { ascending: true })
            .limit(1);

          setInProgressShift({
            ...currentShift,
            actual_arrival_time: timeLogsData?.[0]?.scanned_at || null
          });
        } else {
          setInProgressShift(null);
        }
      } catch (error) {
        console.error('Error fetching in-progress shift:', error);
        setInProgressShift(null);
      } finally {
        setLoading(false);
      }
    };

    fetchInProgressShift();
  }, [employeeId]);

  const formatTimeRemaining = (endTime: string) => {
    const end = parseISO(endTime);
    const minutesRemaining = differenceInMinutes(end, currentTime);
    
    if (minutesRemaining <= 0) {
      return 'Ended';
    }
    
    const hours = Math.floor(minutesRemaining / 60);
    const minutes = minutesRemaining % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };


  if (loading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Current Shift Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">Loading shift information...</p>
          {onNavigateToShifts && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={onNavigateToShifts}
            >
              <Calendar className="h-4 w-4 mr-2" />
              My Shifts
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!inProgressShift) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Current Shift Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">No shift in progress at the moment.</p>
          {onNavigateToShifts && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={onNavigateToShifts}
            >
              <Calendar className="h-4 w-4 mr-2" />
              My Shifts
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Calculate shift status using timezone-aware service
  const shiftStatus = shiftStatusService.calculateShiftStatus(
    parseISO(inProgressShift.start_time),
    parseISO(inProgressShift.end_time),
    employeeId,
    inProgressShift.actual_arrival_time,
    null // No departure time tracking in this component
  );

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Current Shift - In Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Shift Time</p>
            <p className="text-lg font-semibold">
              {format(parseISO(inProgressShift.start_time), 'h:mm a')} - {format(parseISO(inProgressShift.end_time), 'h:mm a')}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">Remaining</p>
            <p className="text-lg font-semibold whitespace-nowrap">
              {formatTimeRemaining(inProgressShift.end_time)}
            </p>
          </div>
        </div>
        
        <ShiftStatusBadge 
          status={shiftStatus}
          variant={StyleVariant.COMPACT}
        />
        
        <div className="text-sm text-muted-foreground">
          <p>Shift Date: {format(parseISO(inProgressShift.start_time), 'EEEE, MMMM d, yyyy')}</p>
          {inProgressShift.actual_arrival_time && (
            <p>Arrived at: {format(parseISO(inProgressShift.actual_arrival_time), 'h:mm a')}</p>
          )}
        </div>
        
        {onNavigateToShifts && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onNavigateToShifts}
          >
            <Calendar className="h-4 w-4 mr-2" />
            My Shifts
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default InProgressShiftDisplay;